import React, { type PropsWithChildren, useMemo } from "react";
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from "react-native";

import { palette, spacing } from "../../styles";
import Typography from "../Typography";
import {
  buttonTypes,
  buttonTypographyTypeMapping,
  buttonTypographyVariantMapping,
  buttonVariants,
} from "./styles";
import type { ButtonProps } from "./types";

const hitSlops = {
  top: 4,
  left: 4,
  right: 4,
  bottom: 4,
};

const Button = ({
  containerStyle,
  disabled,
  label,
  leftComponent,
  loading,
  onPress,
  rightComponent,
  style,
  textStyle,
  type,
  labelType,
  testID,
  variant = "default",
  children,
}: PropsWithChildren<ButtonProps>) => {
  const isLink = useMemo(
    () => ["link", "buttonLink"].includes(buttonTypographyVariantMapping[type]),
    [type],
  );

  return (
    <View style={style}>
      <Pressable
        accessible
        hitSlop={hitSlops}
        disabled={loading || disabled}
        onPress={onPress}
        style={({ pressed }) => [
          styles.container,
          buttonTypes[type],
          buttonVariants[variant],
          (disabled || pressed) && { opacity: 0.6 },
          containerStyle,
        ]}
        testID={testID}
      >
        {loading ? (
          <ActivityIndicator color={palette.common.white} size="small" />
        ) : (
          children || (
            <>
              {leftComponent?.()}
              <Typography
                center
                style={[textStyle, isLink && disabled && { color: palette.font.placeholder }]}
                type={labelType || buttonTypographyTypeMapping[type]}
                variant={buttonTypographyVariantMapping[type]}
              >
                {label}
              </Typography>
              {rightComponent?.()}
            </>
          )
        )}
      </Pressable>
    </View>
  );
};

export default React.memo(Button);

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    minHeight: spacing.unit * 6.75,
    ...Platform.select({
      default: { marginVertical: spacing.unit },
      web: {},
    }),
  },
});
